import React, { useEffect } from 'react'

import { useHistory, useParams } from 'react-router-dom'
import { Box, Button } from '@material-ui/core'
import { FormContext, useForm } from 'react-hook-form'
import { reverse } from 'named-urls'

import { Container, ContentHeader, LoadingFeedback, Page } from 'components'
import {
  PrivacyByDesignForm as PrivacyByDesignGeneralInfoForm,
  PrivacyByDesignQuestionReply,
  PrivacyByDesignReviewActions,
  PrivacyByDesignReviewPanel,
  PrivacyByDesignSendToReviewButton,
} from 'views/PrivacyByDesign/components'

import useFetch from 'hooks/useFetch'

import * as service from 'service'
import helpers from 'helpers'
import { routes } from 'GuestRoutes'
import schema from 'views/PrivacyByDesign/components/PrivacyByDesignForm/schema'
import { buildDefaultValues } from 'views/PrivacyByDesign/components/PrivacyByDesignForm/formMappers'

const RESPONSIBLE_FIELD = 'name'

// Visualização somente leitura do projeto pelo portal de convidados, com os
// mesmos componentes do app.
const PrivacyByDesignShow = () => {
  const history = useHistory()
  const { privacyByDesignId, organizationSlug } = useParams()

  const { response, isLoading, refresh } = useFetch(
    service.guestPortal.privacyByDesign.show,
    { privacyByDesignId },
    [privacyByDesignId],
  )

  const privacyByDesign = response?.data?.privacyByDesign

  const formMethods = useForm({
    validationSchema: schema({
      mode: 'show',
      responsibleField: RESPONSIBLE_FIELD,
    }),
    defaultValues: buildDefaultValues(privacyByDesign, {
      mode: 'show',
      responsibleField: RESPONSIBLE_FIELD,
    }),
  })

  const { reset } = formMethods

  const redirectToList = () =>
    history.push(reverse(routes.privacyByDesign.list, { organizationSlug }))

  // Reabrir devolve o projeto para pendente: quem reabriu vai continuar
  // respondendo, então a tela seguinte é a edição — é lá que ficam o save das
  // questões e o envio para revisão.
  const redirectToEdit = () =>
    history.push(
      reverse(routes.privacyByDesign.edit, {
        organizationSlug,
        privacyByDesignId,
      }),
    )

  useEffect(() => {
    if (privacyByDesign) {
      reset(
        buildDefaultValues(privacyByDesign, {
          mode: 'show',
          responsibleField: RESPONSIBLE_FIELD,
        }),
      )
    }
  }, [privacyByDesign, reset])

  return (
    <Page title="Visualização de Projeto - Privacy By Design">
      <Container maxWidth={false}>
        <LoadingFeedback open={isLoading} />
        {/* Sem breadcrumbs: eles são resolvidos pelas rotas do app e não fazem
            sentido no portal, que tem só a listagem e o projeto. */}
        <ContentHeader
          title={privacyByDesign?.name || 'Privacy By Design'}
          historyBreadcumbs={false}
        />
        <FormContext {...formMethods}>
          {privacyByDesign && (
            <PrivacyByDesignGeneralInfoForm
              mode="show"
              privacyByDesign={privacyByDesign}
              responsibleField={RESPONSIBLE_FIELD}
            />
          )}
          <PrivacyByDesignReviewPanel
            privacyByDesign={privacyByDesign}
            actions={
              <PrivacyByDesignReviewActions
                privacyByDesign={privacyByDesign}
                service={service.guestPortal.privacyByDesign}
                refresh={refresh}
                onReopen={redirectToEdit}
              />
            }
          />
          <PrivacyByDesignQuestionReply
            questions={privacyByDesign?.questions}
            isLoading={isLoading}
            isShow
          />
        </FormContext>
        <Box mt={2} gridGap={5} display="flex" justifyContent="flex-end">
          <Box>
            <Button type="button" variant="outlined" onClick={redirectToList}>
              Voltar
            </Button>
          </Box>
          {/* O envio para revisão também vale a partir da visualização, desde
              que o projeto ainda esteja em um status que permita. */}
          {helpers.privacyByDesign.canSendToReview(privacyByDesign?.status) && (
            <PrivacyByDesignSendToReviewButton
              privacyByDesignId={privacyByDesignId}
              service={service.guestPortal.privacyByDesign}
              questions={privacyByDesign?.questions}
              onSuccess={redirectToList}
            />
          )}
        </Box>
      </Container>
    </Page>
  )
}

export default PrivacyByDesignShow
